/**
 * Toast — shared notification primitive (cycle-101.1 sprint-3 Task 3.6, beads `26gu`).
 *
 * Module-level pub/sub: `pushToast(spec)` from anywhere, `<ToastStack />`
 * mounts once near the app root and renders the active toasts. No React
 * Context required — `onAction` callbacks live in module memory rather than
 * traversing a CustomEvent boundary, so they Just Work.
 *
 * Replaces the bespoke ToastCard render path inside `StorageToastSystem`
 * and unblocks `ForgeLane` dismiss → toast + RESTORE flow per Cycle-101
 * sprint-5 review BLOCK-2 (FORGE dismiss without toast).
 */

import { useEffect, useState } from 'react';

// ── Types ───────────────────────────────────────────────────────────────

export type ToastVariant = 'info' | 'warning' | 'error' | 'success';

export interface ToastAction {
  readonly label: string;
  readonly onAction: () => void;
}

export interface ToastSpec {
  /** Optional explicit id; auto-generated when omitted. */
  readonly id?: string;
  /** Body copy. Plain text — no HTML. */
  readonly message: string;
  /** Visual accent. Default `info`. */
  readonly variant?: ToastVariant;
  /** Optional inline action (e.g. RESTORE / Retry). */
  readonly action?: ToastAction;
  /**
   * Auto-dismiss after N ms. `null` (or omit) = persistent until user
   * clicks ×. Quota toasts pass `null`; ForgeLane dismiss passes 6000.
   */
  readonly durationMs?: number | null;
  /**
   * Optional dedupe key. When two pushes share a `dedupeKey`, the second
   * replaces the first instead of stacking. StorageToastSystem uses
   * `${kind}-${mode}` for this.
   */
  readonly dedupeKey?: string;
  /**
   * Optional override for the rendered card's `data-testid`. Defaults to
   * `toast-card`. Use to keep kind-specific selectors (e.g. `toast-quota`)
   * stable when migrating bespoke toast surfaces onto this primitive.
   */
  readonly dataTestId?: string;
}

interface ActiveToast extends ToastSpec {
  readonly id: string;
}

// ── Module-level store ──────────────────────────────────────────────────

let toasts: readonly ActiveToast[] = [];
const subscribers = new Set<(toasts: readonly ActiveToast[]) => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
let idCounter = 0;

function notify(): void {
  for (const sub of subscribers) sub(toasts);
}

function clearTimer(id: string): void {
  const t = timers.get(id);
  if (t !== undefined) {
    clearTimeout(t);
    timers.delete(id);
  }
}

function genId(): string {
  idCounter += 1;
  return `toast-${Date.now().toString(36)}-${idCounter}`;
}

/** Imperatively push a toast. Returns its id (use with `dismissToast`). */
export function pushToast(spec: ToastSpec): string {
  const id = spec.id ?? genId();
  const next: ActiveToast = { ...spec, id };

  // Dedupe: replace prior toast with same dedupeKey.
  if (spec.dedupeKey !== undefined) {
    const dup = toasts.find((t) => t.dedupeKey === spec.dedupeKey);
    if (dup !== undefined) clearTimer(dup.id);
    toasts = toasts.filter((t) => t.dedupeKey !== spec.dedupeKey);
  }

  toasts = [...toasts, next];

  if (spec.durationMs !== null && spec.durationMs !== undefined) {
    const handle = setTimeout(() => {
      dismissToast(id);
    }, spec.durationMs);
    timers.set(id, handle);
  }

  notify();
  return id;
}

/** Imperatively remove a toast by id. No-op if id is unknown. */
export function dismissToast(id: string): void {
  clearTimer(id);
  if (!toasts.some((t) => t.id === id)) return;
  toasts = toasts.filter((t) => t.id !== id);
  notify();
}

/** Test-only: drop everything. Exported for vitest cleanup. */
export function _resetToastsForTest(): void {
  for (const id of timers.keys()) clearTimer(id);
  toasts = [];
  notify();
}

/** Test-only: peek at the current store. */
export function _getToastsForTest(): readonly ActiveToast[] {
  return toasts;
}

// ── React surface ───────────────────────────────────────────────────────

/** Subscribe to the toast store. Use inside a component. */
function useToasts(): readonly ActiveToast[] {
  const [snapshot, setSnapshot] = useState<readonly ActiveToast[]>(toasts);
  useEffect(() => {
    subscribers.add(setSnapshot);
    setSnapshot(toasts);
    return () => {
      subscribers.delete(setSnapshot);
    };
  }, []);
  return snapshot;
}

export interface ToastStackProps {
  /** Optional override for the wrapper test id. */
  readonly testId?: string;
  /** Optional className passthrough. */
  readonly className?: string;
}

/**
 * Mount once near the app root (e.g. inside the V2 workspace shell). All
 * pushed toasts render here in a fixed-position stack at the bottom-right.
 */
export function ToastStack({ testId = 'toast-stack', className }: ToastStackProps) {
  const stack = useToasts();
  if (stack.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      data-testid={testId}
      className={className}
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxWidth: 360,
        zIndex: 80,
      }}
    >
      {stack.map((toast) => (
        <ToastCard key={toast.id} toast={toast} />
      ))}
    </div>
  );
}

// ── Card ────────────────────────────────────────────────────────────────

const ACCENT_BY_VARIANT: Record<ToastVariant, string> = {
  info: 'var(--cd-sig-iris)',
  warning: 'var(--cd-sig-amber)',
  error: 'var(--cd-sig-ember)',
  success: 'var(--cd-sig-mint)',
};

function ToastCard({ toast }: { toast: ActiveToast }) {
  const variant = toast.variant ?? 'info';
  const accent = ACCENT_BY_VARIANT[variant];

  return (
    <div
      role="alert"
      data-testid={toast.dataTestId ?? 'toast-card'}
      data-toast-id={toast.id}
      data-toast-variant={variant}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        background: 'var(--cd-bg-raised)',
        border: '1px solid var(--cd-border-strong)',
        borderLeft: `3px solid ${accent}`,
        borderRadius: 'var(--cd-radius)',
        boxShadow: 'var(--cd-shadow-2)',
        fontFamily: 'var(--cd-font-sans)',
        color: 'var(--cd-fg)',
        fontSize: 12,
        lineHeight: 1.4,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{toast.message}</span>
      {toast.action !== undefined && (
        <button
          type="button"
          data-testid="toast-card-action"
          onClick={() => {
            toast.action!.onAction();
            dismissToast(toast.id);
          }}
          style={{
            padding: '4px 10px',
            fontFamily: 'var(--cd-font-mono)',
            fontSize: 10,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            color: 'var(--cd-bg)',
            background: 'var(--cd-fg)',
            border: 'none',
            borderRadius: 'var(--cd-radius)',
            cursor: 'pointer',
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => dismissToast(toast.id)}
        aria-label="Dismiss notification"
        data-testid="toast-card-dismiss"
        style={{
          width: 22,
          height: 22,
          color: 'var(--cd-fg-muted)',
          background: 'transparent',
          border: '1px solid var(--cd-border)',
          borderRadius: 'var(--cd-radius)',
          cursor: 'pointer',
          fontFamily: 'var(--cd-font-mono)',
          fontSize: 13,
          padding: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
